<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar class="custom-toolbar">
        <ion-buttons slot="start">
          <ion-menu-button color="light"></ion-menu-button>
        </ion-buttons>
        <ion-title class="custom-title">My Leave History</ion-title>
        <ion-buttons slot="end">
          <ion-button @click="fetchRequisitions" color="light" class="refresh-btn">
            <ion-icon slot="icon-only" :icon="refreshOutline"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content :fullscreen="true" class="leave-content">
      <div class="leave-container">
        
        <!-- Summary Cards Section -->
        <div v-if="!loading && requisitions.length > 0" class="summary-row">
          <div class="summary-pill total">
            <span class="pill-number">{{ requisitions.length }}</span>
            <span class="pill-label">Total Applied</span>
          </div>
          <div class="summary-pill approved">
            <span class="pill-number">{{ getCountByStatus('approved') }}</span>
            <span class="pill-label">Approved</span>
          </div>
          <div class="summary-pill pending">
            <span class="pill-number">{{ getCountByStatus('pending') }}</span>
            <span class="pill-label">Pending</span>
          </div>
        </div>

        <!-- Requisitions List -->
        <div v-if="loading" class="loading-state">
          <ion-spinner name="crescent" color="primary"></ion-spinner>
          <span>Retrieving leave history...</span>
        </div>

        <div v-else-if="requisitions.length > 0" class="requisitions-list">
          <div
            v-for="req in requisitions"
            :key="req.id"
            class="requisition-card"
            :class="[displayStatus(req), pushFocusClass(req.id)]"
            :data-push-id="req.id"
          >
            <div class="card-glow"></div>
            
            <div class="card-header-row">
              <div class="type-info">
                <div class="type-icon-wrapper">
                  <ion-icon :icon="getLeaveIcon(req.leave_type ? req.leave_type.title : '')" class="type-icon"></ion-icon>
                </div>
                <div>
                  <h3 class="leave-title">{{ req.leave_type ? req.leave_type.title : 'Leave Application' }}</h3>
                  <span class="applied-date">Applied on {{ formatDate(req.created_at || req.start_date) }}</span>
                </div>
              </div>
              <div class="status-badge" :class="displayStatus(req)">
                <span class="status-dot"></span>
                <span>{{ displayStatus(req) }}</span>
              </div>
            </div>

            <div class="card-body">
              <!-- Dates Bento Box -->
              <div class="dates-bento">
                <div class="date-col">
                  <span class="date-label">FROM</span>
                  <span class="date-val">{{ formatDateShort(req.start_date) }}</span>
                </div>
                <div class="duration-arrow">
                  <ion-icon :icon="arrowForwardOutline"></ion-icon>
                  <span class="duration-pill">
                    <template v-if="getRecalledCount(req) > 0">{{ getEffectiveDays(req) }} of {{ req.num_of_days }} Days</template>
                    <template v-else>{{ req.num_of_days }} Days</template>
                  </span>
                </div>
                <div class="date-col">
                  <span class="date-label">TO</span>
                  <span class="date-val">{{ formatDateShort(req.end_date) }}</span>
                </div>
              </div>

              <!-- Recall note (shown when one or more days were recalled by Admin) -->
              <div v-if="getRecalledCount(req) > 0" class="recall-note">
                <ion-icon :icon="returnUpBackOutline" class="recall-icon"></ion-icon>
                <div class="recall-body">
                  <span class="recall-title">{{ getRecalledCount(req) }} day(s) recalled by Admin</span>
                  <div class="recall-dates">
                    <span v-for="entry in req.recalled_dates" :key="entry.date" class="recall-chip">
                      {{ formatDateShort(entry.date) }}<template v-if="entry.reason"> — {{ entry.reason }}</template>
                    </span>
                  </div>
                </div>
              </div>

              <!-- Reason section -->
              <div class="reason-section">
                <ion-icon :icon="chatboxEllipsesOutline" class="reason-icon"></ion-icon>
                <p class="reason-text">"{{ req.reason || 'No reason specified' }}"</p>
              </div>

              <!-- Attachment Section (if available) -->
              <div v-if="req.attachment" class="attachment-preview-box">
                <ion-icon :icon="documentAttachOutline" class="attach-icon"></ion-icon>
                <a :href="'https://jahaji-admin.test/' + req.attachment" target="_blank" class="attachment-link">
                  View Uploaded Attachment
                </a>
              </div>
            </div>

            <!-- Approver Timeline Footer -->
            <div class="card-footer" :class="displayStatus(req)">
              <div v-if="displayStatus(req) === 'recalled'" class="footer-msg text-recalled">
                <ion-icon :icon="returnUpBackOutline"></ion-icon>
                <span>Fully recalled by Admin — no longer counted as leave</span>
              </div>
              <div v-else-if="req.approve_status === 'approved'" class="footer-msg text-success">
                <ion-icon :icon="checkmarkCircleOutline"></ion-icon>
                <span>Approved by <strong>{{ req.approved_by_user ? req.approved_by_user.name : 'Manager' }}</strong></span>
              </div>
              <div v-else-if="req.approve_status === 'declined'" class="footer-msg text-danger">
                <ion-icon :icon="closeCircleOutline"></ion-icon>
                <div class="decline-box">
                  <span>Declined: <strong>{{ req.decline_reason || 'No details provided' }}</strong></span>
                </div>
              </div>
              <div v-else class="footer-msg text-warning">
                <ion-icon :icon="timeOutline"></ion-icon>
                <span>Currently with <strong>{{ req.current_approver ? req.current_approver.name : 'Reporting Boss' }}</strong></span>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State Redesign -->
        <div v-else class="empty-state">
          <div class="empty-icon-wrapper">
            <ion-icon :icon="calendarClearOutline" class="empty-icon"></ion-icon>
          </div>
          <h2>No Leave History</h2>
          <p>You haven't submitted any leave requests yet. When you do, they will appear here with their live approval tracking statuses.</p>
          <button class="btn-apply-now" @click="goToApply">
            <span>Apply For Leave</span>
            <ion-icon :icon="arrowForwardOutline"></ion-icon>
          </button>
        </div>

      </div>
    </ion-content>
  </ion-page>
</template>

<script>
/* eslint-disable */
import { 
  IonButtons, 
  IonContent, 
  IonHeader, 
  IonMenuButton, 
  IonPage, 
  IonTitle, 
  IonToolbar, 
  IonButton, 
  IonIcon,
  IonSpinner
} from '@ionic/vue';
import { 
  refreshOutline, 
  briefcaseOutline,
  medicalOutline, 
  happyOutline, 
  airplaneOutline, 
  documentTextOutline,
  chatboxEllipsesOutline,
  checkmarkCircleOutline,
  closeCircleOutline,
  timeOutline,
  arrowForwardOutline,
  calendarClearOutline,
  documentAttachOutline,
  returnUpBackOutline
} from 'ionicons/icons';
import { pushFocus } from '../../pushNotifications';

export default {
  name: 'MyLeaves',
  mixins: [pushFocus],
  components: {
    IonButtons,
    IonContent,
    IonHeader,
    IonMenuButton,
    IonPage,
    IonTitle,
    IonToolbar,
    IonButton,
    IonIcon,
    IonSpinner
  },
  data() {
    return {
      // Icons
      refreshOutline,
      briefcaseOutline,
      chatboxEllipsesOutline,
      checkmarkCircleOutline,
      closeCircleOutline,
      timeOutline,
      arrowForwardOutline,
      calendarClearOutline,
      documentAttachOutline,
      returnUpBackOutline,

      requisitions: [],
      loading: true,
    }
  },
  created() {
    this.fetchRequisitions();
  },
  methods: {
    reloadForPush() {
      this.fetchRequisitions();
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
      } catch (e) {
        return dateStr;
      }
    },
    formatDateShort(dateStr) {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
      } catch (e) {
        return dateStr;
      }
    },
    getLeaveIcon(title) {
      if (!title) return documentTextOutline;
      const t = title.toLowerCase();
      if (t.includes('sick')) return medicalOutline;
      if (t.includes('casual')) return happyOutline;
      if (t.includes('annual') || t.includes('earn')) return airplaneOutline;
      return documentTextOutline;
    },
    getRecalledCount(req) {
      return (req.recalled_dates && req.recalled_dates.length) ? req.recalled_dates.length : 0;
    },
    getEffectiveDays(req) {
      const eff = req.num_of_days - this.getRecalledCount(req);
      return eff > 0 ? eff : 0;
    },
    displayStatus(req) {
      // An approved leave whose every day has been recalled is shown as "recalled"
      // rather than "approved" so it no longer reads as active leave in history.
      if (req.approve_status === 'approved' && this.getRecalledCount(req) > 0 && this.getEffectiveDays(req) === 0) {
        return 'recalled';
      }
      return req.approve_status;
    },
    getCountByStatus(status) {
      return this.requisitions.filter(r => this.displayStatus(r) === status).length;
    },
    goToApply() {
      this.$router.push('/hr/leave-apply');
    },
    fetchRequisitions() {
      this.loading = true;
      const data = {
        username: window.localStorage.username,
        password: window.localStorage.password,
      };

      this.$http.post('hr/myLeaveRequisitions', data, { skipLoading: true })
        .then(response => {
          if (response.data.status) {
            this.requisitions = response.data.requisitions;
          }
          this.loading = false;
          // Opened from a notification (approved / declined / recalled): show that leave.
          this.focusPushCard();
        })
        .catch(err => {
          console.error(err);
          this.loading = false;
        });
    }
  }
}
</script>

<style scoped>
/* Core Styles */
.leave-content {
  --background: #f4f6fc;
}

.leave-container {
  padding: 16px;
  max-width: 600px;
  margin: 0 auto;
}

/* Custom Toolbar Styles */
.custom-toolbar {
  --background: linear-gradient(135deg, #102a43 0%, #243b53 100%);
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.custom-title {
  font-weight: 700;
  font-size: 1.15rem;
  letter-spacing: 0.5px;
}

.refresh-btn {
  --color: #fff;
}

/* Loading state */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  gap: 12px;
  color: #627d98;
}

/* Summary stats bar */
.summary-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 20px;
}

.summary-pill {
  flex: 1;
  background: #fff;
  padding: 12px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 4px 15px rgba(16, 42, 67, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.01);
}

.pill-number {
  font-size: 1.4rem;
  font-weight: 800;
  color: #102a43;
}

.pill-label {
  font-size: 0.68rem;
  font-weight: 700;
  color: #829ab1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

.summary-pill.approved .pill-number {
  color: #10b981;
}

.summary-pill.pending .pill-number {
  color: #f59e0b;
}

/* Custom Bento Requisition Card */
.requisition-card {
  position: relative;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(16, 42, 67, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.02);
  margin-bottom: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.requisition-card:active {
  transform: scale(0.98);
}

.card-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
}

/* Status-based colors & glow lines */
.requisition-card.approved .card-glow {
  background: linear-gradient(90deg, #10b981 0%, #059669 100%);
}

.requisition-card.pending .card-glow {
  background: linear-gradient(90deg, #f59e0b 0%, #d97706 100%);
}

.requisition-card.declined .card-glow {
  background: linear-gradient(90deg, #ef4444 0%, #dc2626 100%);
}

.requisition-card.recalled .card-glow {
  background: linear-gradient(90deg, #94a3b8 0%, #64748b 100%);
}

/* Card Header */
.card-header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 16px 12px 16px;
}

.type-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.type-icon-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f4f8;
}

.requisition-card.approved .type-icon-wrapper {
  background: #f0fdf4;
}
.requisition-card.approved .type-icon {
  color: #10b981;
}

.requisition-card.pending .type-icon-wrapper {
  background: #fffbeb;
}
.requisition-card.pending .type-icon {
  color: #d97706;
}

.requisition-card.declined .type-icon-wrapper {
  background: #fff5f5;
}
.requisition-card.declined .type-icon {
  color: #ef4444;
}

.requisition-card.recalled .type-icon-wrapper {
  background: #f1f5f9;
}
.requisition-card.recalled .type-icon {
  color: #64748b;
}

.type-icon {
  font-size: 1.3rem;
  color: #486581;
}

.leave-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #102a43;
}

.applied-date {
  font-size: 0.75rem;
  color: #829ab1;
  font-weight: 500;
}

/* Status Badge */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: capitalize;
}

.status-badge .status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.status-badge.approved {
  background: #d1fae5;
  color: #065f46;
}
.status-badge.approved .status-dot {
  background: #10b981;
}

.status-badge.pending {
  background: #fef3c7;
  color: #92400e;
}
.status-badge.pending .status-dot {
  background: #f59e0b;
}

.status-badge.declined {
  background: #fee2e2;
  color: #991b1b;
}
.status-badge.declined .status-dot {
  background: #ef4444;
}

.status-badge.recalled {
  background: #e2e8f0;
  color: #475569;
}
.status-badge.recalled .status-dot {
  background: #64748b;
}

/* Card Body */
.card-body {
  padding: 0 16px 16px 16px;
}

/* Dates Bento Box */
.dates-bento {
  background: #f8fafc;
  border-radius: 12px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #e2e8f0;
  margin-bottom: 12px;
}

.date-col {
  display: flex;
  flex-direction: column;
}

.date-label {
  font-size: 0.65rem;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.5px;
}

.date-val {
  font-size: 0.9rem;
  font-weight: 700;
  color: #334e68;
}

.duration-arrow {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  color: #94a3b8;
}

.duration-arrow ion-icon {
  font-size: 1.1rem;
}

.duration-pill {
  background: #e2e8f0;
  color: #475569;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 8px;
}

/* Reason section */
.reason-section {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-bottom: 12px;
}

.reason-icon {
  font-size: 1.1rem;
  color: #94a3b8;
  margin-top: 2px;
}

.reason-text {
  margin: 0;
  font-size: 0.85rem;
  font-style: italic;
  color: #475569;
  line-height: 1.4;
}

/* Attachment preview */
.attachment-preview-box {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #f1f5f9;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.attach-icon {
  color: #64748b;
  font-size: 1.1rem;
}

.attachment-link {
  font-size: 0.8rem;
  font-weight: 600;
  color: #2563eb;
  text-decoration: none;
}

.attachment-link:hover {
  text-decoration: underline;
}

/* Requisition Card Footer */
.card-footer {
  padding: 12px 16px;
  border-top: 1px dashed #e2e8f0;
  background: #fafafa;
  font-size: 0.82rem;
}

.footer-msg {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

.footer-msg ion-icon {
  font-size: 1.15rem;
}

.text-success {
  color: #10b981;
}

.text-danger {
  color: #ef4444;
}

.text-warning {
  color: #d97706;
}

.text-recalled {
  color: #64748b;
}

/* Recall note in card body */
.recall-note {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 10px;
  padding: 8px 12px;
  margin-bottom: 12px;
}

.recall-icon {
  font-size: 1.1rem;
  color: #ea580c;
  margin-top: 2px;
  flex-shrink: 0;
}

.recall-title {
  display: block;
  font-size: 0.8rem;
  font-weight: 700;
  color: #c2410c;
  margin-bottom: 4px;
}

.recall-dates {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.recall-chip {
  background: #ffedd5;
  color: #9a3412;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 6px;
}

.decline-box {
  display: flex;
  flex-direction: column;
}

/* Empty State Styling */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;
  margin-top: 32px;
}

.empty-icon-wrapper {
  background: #e0f2fe;
  padding: 20px;
  border-radius: 50%;
  margin-bottom: 16px;
}

.empty-icon {
  font-size: 3rem;
  color: #0284c7;
}

.empty-state h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #102a43;
  margin: 0 0 8px 0;
}

.empty-state p {
  font-size: 0.88rem;
  color: #627d98;
  margin: 0 0 24px 0;
  max-width: 300px;
  line-height: 1.5;
}

.btn-apply-now {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #0072ff 0%, #00c6ff 100%);
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-weight: 700;
  font-size: 0.9rem;
  box-shadow: 0 4px 15px rgba(0, 114, 255, 0.2);
}

.btn-apply-now:active {
  transform: scale(0.98);
}
</style>
